import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Button, Link, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AxiosError } from 'axios';
import React, { CSSProperties, FC, PropsWithChildren, useState } from 'react';
import { ForesightSupportEmail } from '../../constants/foresight';
import {
  DynamicImportReloadKey,
  isDynamicImportError,
  setStaleChunkReloading,
  wasReloadRecentlyAttempted,
} from '../../util/dynamicImportError';
import { isWarmingUp } from '../../util/lazyPage';
import { useHideBootLoader } from '../Loader/BootLoader';

// Guarded auto-reload for stale-chunk failures (a release replaced the hashed
// assets while this tab was open). Returns false when a reload was already
// attempted recently, so callers fall back to surfacing the error instead of looping.
export function attemptDynamicImportReload(): boolean {
  if (wasReloadRecentlyAttempted()) {
    return false;
  }
  sessionStorage.setItem(DynamicImportReloadKey, String(Date.now()));
  // Sentry's beforeSend drops the chunk errors this page reports on its way out; the reload is the fix.
  setStaleChunkReloading(true);
  window.location.reload();
  return true;
}

let reloadHolds = 0;

/**
 * Stops a failed chunk download from reloading the page until the returned release runs, for UI
 * that shows the failure itself (an open document viewer) over a page that may hold unsaved work.
 */
export function holdDynamicImportReload(): () => void {
  reloadHolds++;
  let released = false;
  return () => {
    if (!released) {
      released = true;
      reloadHolds--;
    }
  };
}

export function registerDynamicImportReloadListener() {
  const onPreloadError = (event: VitePreloadErrorEvent) => {
    // A failed background download must not reload the page under the user; the click that
    // needs that page downloads it again and reloads from there if it still fails.
    if (isWarmingUp() || reloadHolds > 0) {
      return;
    }
    if (attemptDynamicImportReload()) {
      // suppress Vite's rethrow; the page is already reloading
      event.preventDefault();
    }
  };
  window.addEventListener('vite:preloadError', onPreloadError);
  return () => window.removeEventListener('vite:preloadError', onPreloadError);
}

const Wrapper = styled('div')`
  background-color: white;
  box-shadow:
    0px -4px 40px rgba(255, 255, 255, 0.06),
    0px 4px 24px rgba(16, 37, 62, 0.04),
    0px 24px 48px rgba(75, 82, 93, 0.02);
  border-radius: 4px;
  padding: 1rem;
  overflow-y: auto;
`;

const DetailsWrapper = styled('div')`
  margin-top: 1rem;
  background-color: ${({ theme }) => theme.colors.primary[5]};
  padding: 1.5rem;
`;

const ButtonsWrapper = styled('div')`
  display: grid;
  grid-template-columns: max-content max-content;
  margin-top: 1rem;
  gap: 1rem;
`;

export interface ISentryBoundaryFallbackProps {
  error: unknown;
  componentStack?: string | null;
  eventId?: string | null;
  resetError?: () => void;
}

export interface IForbiddenErrorConfig {
  title?: string;
  message?: string | React.ReactNode;
  showContactSupport?: boolean;
}

export interface IGenericErrorFallbackProps extends ISentryBoundaryFallbackProps {
  style?: CSSProperties;
  forbiddenConfig?: IForbiddenErrorConfig;
}

export function GenericErrorFallback({
  error,
  style,
  resetError,
  forbiddenConfig,
}: IGenericErrorFallbackProps) {
  // An error in place of the shell must show, not sit behind the index.html loader.
  useHideBootLoader();
  const [showMore, setShowMore] = useState(false);

  if (isDynamicImportError(error)) {
    if (attemptDynamicImportReload()) {
      return null;
    }
    // Recent reload didn't help - fall through to show error UI
    // Don't clear sessionStorage here to avoid re-render loop; let it expire naturally via timeout
  }

  if ((error as AxiosError).response?.status === 403) {
    return <ForbiddenError error={error} style={style} config={forbiddenConfig} />;
  }

  const errorDetails = (error as Error).stack ?? String(error);

  return (
    <Wrapper style={style}>
      <ErrorTitle>It seems something went wrong</ErrorTitle>
      <div>
        <Button color='secondary' onClick={() => setShowMore((prev) => !prev)} sx={{ margin: '.25rem' }}>
          {showMore ? 'Hide technical details' : 'View technical details'}
        </Button>
      </div>
      {showMore && (
        <DetailsWrapper>
          <Typography variant={'caption'}>
            {new Date().toISOString()}
            {errorDetails}
          </Typography>
        </DetailsWrapper>
      )}
      <ButtonsWrapper>
        {resetError && (
          <Button onClick={resetError} variant='contained' color='secondary'>
            Try Reload Section
          </Button>
        )}
        <Button onClick={() => window.location.reload()} variant='contained'>
          Reload page
        </Button>
      </ButtonsWrapper>
    </Wrapper>
  );
}

export interface IForbiddenErrorProps extends IGenericErrorFallbackProps {
  config?: IForbiddenErrorConfig;
}

export const ForbiddenError: FC<IForbiddenErrorProps> = ({ config }) => {
  const title = config?.title ?? 'Forbidden';
  const message = config?.message ?? 'Sorry! It seems like you do not have access to this page.';
  const showContactSupport = config?.showContactSupport ?? true;

  return (
    <Wrapper style={{ display: 'grid', height: '100%', alignContent: 'start' }}>
      <ErrorTitle>{title}</ErrorTitle>
      <DetailsWrapper>
        <Typography variant='body2' component={'div'}>
          {typeof message === 'string' ? <p>{message}</p> : message}
          {showContactSupport && (
            <p>
              Please contact <Link href={`mailto:${ForesightSupportEmail}`}>{ForesightSupportEmail}</Link> for
              further assistance.
            </p>
          )}
        </Typography>
      </DetailsWrapper>
    </Wrapper>
  );
};
export const ErrorTitle: FC<PropsWithChildren> = ({ children }) => {
  return (
    <Typography variant='body1' display='flex' alignItems={'center'} gap='0.25rem'>
      <ErrorOutlineIcon color='warning' />
      {children}
    </Typography>
  );
};

export function ForbiddenFallback({ error }: { error: Error }) {
  console.error(error);
  return <ForbiddenError error={error} />;
}

export function NotFoundFallback({ message }: { message?: string }) {
  const displayMessage = message ?? 'The resource you are looking for could not be found.';

  return (
    <Wrapper style={{ display: 'grid', height: '100%', alignContent: 'start' }}>
      <ErrorTitle>Not Found</ErrorTitle>
      <DetailsWrapper>
        <Typography variant='body2' component='div'>
          <p>{displayMessage}</p>
        </Typography>
      </DetailsWrapper>
    </Wrapper>
  );
}
